import React, { useState, useEffect } from 'react';
import { 
  Box, Button, TextField, Paper, Typography, FormControlLabel, Switch, 
  Grid, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Card,
  CardContent, CardHeader, Divider, Alert, IconButton, InputAdornment,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  ArrowBack, Save, Preview, Add, Close, Movie, Image, 
  Link, Article, Edit, Tv, AttachMoney, SportsBasketball, Computer
} from '@mui/icons-material';
import newsOperations from '../../../services/newsApi';
import MovieSearchModal from './MovieSearchModal';
import RichTextEditor from './RichTextEditor';
import NewsPreview from './NewsPreview';
import ImageUpload from './ImageUpload';

const EditNewsForm = ({ news, onBack, onSuccess }) => {
  const [formData, setFormData] = useState({});
  const [movieModalOpen, setMovieModalOpen] = useState(false);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (news) {
      setFormData({
        title: news.title || '',
        description: news.description || '',
        content: news.content || '',
        source: news.source || '',
        source_url: news.source_url || '',
        author: news.author || '',
        published_at: news.published_at 
          ? new Date(news.published_at).toISOString().slice(0, 16)
          : new Date().toISOString().slice(0, 16),
        featured_image: news.featured_image || '',
        tmdb_id: news.tmdb_id || null,
        category: news.category || 'movies',
        entity_id: news.entity_id || null,
        entity_type: news.entity_type || null,
        entity_name: news.entity_name || '',
        status: news.status || 'draft',
        is_featured: news.is_featured || false,
        is_breaking: news.is_breaking || false,
      });
      setTags(news.tags || []);
      if (news.tmdb_id) {
        // Set selected movie if tmdb_id exists
        setSelectedMovie({ id: news.tmdb_id, tmdb_id: news.tmdb_id });
      }
    }
  }, [news]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleMovieSelect = (movie) => {
    setSelectedMovie(movie);
    setFormData(prev => ({ 
      ...prev, 
      tmdb_id: movie.id,
      entity_id: movie.id.toString(),
      entity_type: formData.category === 'movies' ? 'movie' : 'tv_show',
      entity_name: movie.name || movie.title
    }));
    setMovieModalOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const result = await newsOperations.updateNews(news.id, {
        ...formData,
        tags,
        entity_type: formData.entity_type || (
          formData.category === 'movies' ? 'movie' :
          formData.category === 'tv_shows' ? 'tv_show' :
          formData.category === 'finance' ? 'company' :
          formData.category === 'sports' ? 'sports_team' :
          'other'
        )
      });
      if (result.success) {
        alert('News updated successfully!');
        onSuccess();
      } else {
        alert('Failed to update news: ' + result.error);
      }
    } catch (err) {
      console.error('Update news error:', err);
      alert('Failed to update news: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={onBack}
          sx={{ borderRadius: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" fontWeight="bold">
          Edit News Article
        </Typography>
        {news && (
          <Chip 
            label={`ID: ${news.newsId}`} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        )}
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Main Content Card */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 3 }}>
              <CardHeader 
                title="Article Content" 
                avatar={<Edit color="primary" />}
              />
              <CardContent>
                <Grid container spacing={3}>
                  {/* Title */}
                  <Grid item xs={12}>
                    <TextField 
                      name="title" 
                      label="Title" 
                      value={formData.title || ''} 
                      onChange={handleChange} 
                      fullWidth 
                      required
                      placeholder="Enter compelling news title..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>

                  {/* Description */}
                  <Grid item xs={12}>
                    <TextField 
                      name="description" 
                      label="Description" 
                      value={formData.description || ''} 
                      onChange={handleChange} 
                      fullWidth 
                      multiline 
                      rows={3}
                      placeholder="Brief description or summary..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>

                  {/* Rich Text Content */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Article Content *
                    </Typography>
                    <RichTextEditor
                      value={formData.content || ''}
                      onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                      placeholder="Write your detailed news article content here..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Media Upload Card */}
              <Card sx={{ borderRadius: 3 }}>
                <CardHeader 
                  title="Media" 
                  avatar={<Image color="primary" />}
                />
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Featured Image
                  </Typography>
                  <ImageUpload
                    value={formData.featured_image || ''}
                    onChange={(url) => setFormData(prev => ({ ...prev, featured_image: url }))}
                  />
                </CardContent>
              </Card>

              {/* Article Details Card */}
              <Card sx={{ borderRadius: 3 }}>
                <CardHeader 
                  title="Article Details" 
                  avatar={<Article color="primary" />}
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField 
                        name="source" 
                        label="Source" 
                        value={formData.source || ''} 
                        onChange={handleChange} 
                        fullWidth
                        placeholder="News source"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        name="source_url" 
                        label="Source URL" 
                        value={formData.source_url || ''} 
                        onChange={handleChange} 
                        fullWidth
                        placeholder="https://..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Link />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        name="author" 
                        label="Author" 
                        value={formData.author || ''} 
                        onChange={handleChange} 
                        fullWidth
                        placeholder="Author name"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        name="published_at" 
                        label="Publish Date & Time" 
                        type="datetime-local" 
                        value={formData.published_at || ''} 
                        onChange={handleChange} 
                        fullWidth 
                        required
                        InputLabelProps={{ shrink: true }}
                        helperText="Article will be sorted by this date and time"
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Category *</InputLabel>
                        <Select
                          name="category"
                          value={formData.category || 'movies'}
                          onChange={handleChange}
                          label="Category *"
                          required
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="movies">
                            <Box display="flex" alignItems="center" gap={1}>
                              <Movie /> Movies
                            </Box>
                          </MenuItem>
                          <MenuItem value="tv_shows">
                            <Box display="flex" alignItems="center" gap={1}>
                              <Tv /> TV Shows
                            </Box>
                          </MenuItem>
                          <MenuItem value="finance">
                            <Box display="flex" alignItems="center" gap={1}>
                              <AttachMoney /> Finance
                            </Box>
                          </MenuItem>
                          <MenuItem value="sports">
                            <Box display="flex" alignItems="center" gap={1}>
                              <SportsBasketball /> Sports
                            </Box>
                          </MenuItem>
                          <MenuItem value="technology">
                            <Box display="flex" alignItems="center" gap={1}>
                              <Computer /> Technology
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Tags Card */}
              <Card sx={{ borderRadius: 3 }}>
                <CardHeader title="Tags" />
                <CardContent>
                  <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    {tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag}
                        onDelete={() => setTags(prev => prev.filter((_, i) => i !== index))}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  <Box display="flex" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Add tag"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newTag.trim()) {
                          e.preventDefault();
                          setTags(prev => [...prev, newTag.trim()]);
                          setNewTag('');
                        }
                      }}
                      sx={{ 
                        flexGrow: 1,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 } 
                      }}
                    />
                    <IconButton
                      onClick={() => {
                        if (newTag.trim()) {
                          setTags(prev => [...prev, newTag.trim()]);
                          setNewTag('');
                        }
                      }}
                      color="primary"
                    >
                      <Add />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>

              {/* Entity Association Card */}
              <Card sx={{ borderRadius: 3 }}>
                <CardHeader 
                  title={
                    formData.category === 'movies' ? 'Movie Association' :
                    formData.category === 'tv_shows' ? 'TV Show Association' :
                    formData.category === 'finance' ? 'Company Association' :
                    formData.category === 'sports' ? 'Team Association' :
                    'Entity Association'
                  }
                  avatar={
                    formData.category === 'movies' ? <Movie color="primary" /> :
                    formData.category === 'tv_shows' ? <Tv color="primary" /> :
                    <Article color="primary" />
                  }
                />
                <CardContent>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {(formData.category === 'movies' || formData.category === 'tv_shows') && (
                      <Button 
                        variant="outlined" 
                        onClick={() => setMovieModalOpen(true)}
                        sx={{ borderRadius: 2 }}
                      >
                        {formData.tmdb_id ? 
                          `${formData.category === 'movies' ? 'Movie' : 'Show'} ID: ${formData.tmdb_id}` : 
                          `Associate ${formData.category === 'movies' ? 'Movie' : 'TV Show'}`
                        }
                      </Button>
                    )}
                    
                    {!['movies', 'tv_shows'].includes(formData.category) && (
                      <>
                        <TextField
                          label="Entity Name"
                          placeholder={
                            formData.category === 'finance' ? 'Company name (e.g., Apple Inc.)' :
                            formData.category === 'sports' ? 'Team name (e.g., Lakers)' :
                            'Entity name'
                          }
                          value={formData.entity_name || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            entity_name: e.target.value,
                            entity_id: e.target.value.toLowerCase().replace(/\s+/g, '_')
                          }))}
                          fullWidth
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Optional: Associate this news with a specific {
                            formData.category === 'finance' ? 'company' :
                            formData.category === 'sports' ? 'team' :
                            'entity'
                          }
                        </Typography>
                      </>
                    )}
                    
                    {formData.tmdb_id && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          {formData.entity_name || `ID: ${formData.tmdb_id}`}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedMovie(null);
                            setFormData(prev => ({ 
                              ...prev, 
                              tmdb_id: null,
                              entity_id: null,
                              entity_name: ''
                            }));
                          }}
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {/* Publishing Options Card */}
              <Card sx={{ borderRadius: 3 }}>
                <CardHeader title="Publishing Options" />
                <CardContent>
                  <Box display="flex" flexDirection="column" gap={2}>
                    <FormControlLabel 
                      control={
                        <Switch 
                          name="is_featured" 
                          checked={formData.is_featured || false} 
                          onChange={handleChange} 
                        />
                      } 
                      label="Featured Article" 
                    />
                    <FormControlLabel 
                      control={
                        <Switch 
                          name="is_breaking" 
                          checked={formData.is_breaking || false} 
                          onChange={handleChange} 
                        />
                      } 
                      label="Breaking News" 
                    />
                    <Divider />
                    <FormControlLabel 
                      control={
                        <Switch 
                          checked={formData.status === 'published'} 
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            status: e.target.checked ? 'published' : 'draft' 
                          }))}
                        />
                      } 
                      label="Publish Immediately" 
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box display="flex" gap={2} justifyContent="center">
                  <Button 
                    variant="outlined" 
                    startIcon={<Preview />} 
                    onClick={() => setPreviewOpen(true)}
                    sx={{ borderRadius: 2, minWidth: 120 }}
                  >
                    Preview
                  </Button>
                  <Button 
                    type="submit" 
                    variant="contained" 
                    startIcon={<Save />} 
                    disabled={saving}
                    sx={{ borderRadius: 2, minWidth: 120 }}
                  >
                    {saving ? 'Updating...' : 'Update News'}
                  </Button>
                  <Button 
                    onClick={onBack}
                    sx={{ borderRadius: 2, minWidth: 120 }}
                  >
                    Cancel
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>

      {/* Modals */}
      <MovieSearchModal 
        open={movieModalOpen} 
        onClose={() => setMovieModalOpen(false)} 
        onSelect={handleMovieSelect} 
      />
      
      <Dialog 
        open={previewOpen} 
        onClose={() => setPreviewOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Preview News Article</DialogTitle>
        <DialogContent>
          <NewsPreview news={{ ...formData, tags }} movie={selectedMovie} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} sx={{ borderRadius: 2 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditNewsForm;
